// RepLayout , persistent shell for all /rep/* routes.
//
// Solves the sidebar-reset-on-nav bug: previously each rep page mounted its own
// <RepDesktopShell> with local useState, so every navigation remounted the shell
// and reset sidebar mode to 'open'. RepLayout sits above the route outlet, so
// its useState survives across all /rep/* transitions.
//
// Structure:
//   RepLayout (holds mode: RepSidebarMode in useState)
//   ├── RepNewspaperSidebar  ← state lives HERE, never remounts
//   └── <main> wrapper
//       └── <Outlet />      ← rep pages render their content body here
//
// Active tab is derived from useLocation().pathname , no prop drilling.
//
// Sidebar context: pages/sub-components that need to control sidebar mode
// (e.g., a "hide sidebar" action deeper in the tree) consume RepSidebarContext.
//
// RepDesktopShell: the per-page wrapper is now unused for routed pages. It is
// kept in RepDesktopShell.tsx as a named export to avoid breaking any future
// one-off usage, but it is no longer instantiated by any routed page.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { RepNewspaperSidebar } from './RepDesktopShell';
import type { RepActiveTab, RepSidebarMode } from './RepDesktopShell';
import { ChefTabBar } from '../chef/ChefTabBar';
import type { TabDef } from '../chef/ChefTabBar';
import { useAuth } from '../../contexts/AuthContext';

// ─── Role label ───────────────────────────────────────────────────────────────
// The "Working as" block previously showed only the rep's name and their
// distributor's name, no role. After the CJ identity split, the rep user and
// the distributor_admin user are distinct logins, so the sidebar must state
// which role the CURRENT session is acting as, not just who is signed in.
// Same ROLE_LABEL/roleLabel pattern already used in CCLayout.tsx (and
// pages/SettingsPage.tsx, pages/admin/QMAdminUsers.tsx,
// pages/admin/QMAdminUserDetail.tsx), kept local rather than extracted into a
// shared util to stay in scope for this change.
const ROLE_LABEL: Record<string, string> = {
  distributor_admin: 'Distributor Admin',
  quoteme_admin: 'QM Admin',
  rep: 'Rep',
  chef: 'Chef',
  buyer: 'Buyer',
  group_admin: 'Group Admin',
  brand: 'Brand',
};

function roleLabel(role: string | undefined): string {
  if (!role) return 'Rep';
  return ROLE_LABEL[role] ?? role;
}

// ─── Sidebar context ──────────────────────────────────────────────────────────

interface RepSidebarContextValue {
  mode: RepSidebarMode;
  setMode: (m: RepSidebarMode) => void;
}

export const RepSidebarContext = createContext<RepSidebarContextValue>({
  mode: 'open',
  setMode: () => {},
});

export function useRepSidebar() {
  return useContext(RepSidebarContext);
}

// ─── Active tab derivation ────────────────────────────────────────────────────

function activeTabFromPath(pathname: string): RepActiveTab {
  if (pathname.startsWith('/rep/quotes/history')) return 'quotes-history';
  if (pathname.startsWith('/rep/quotes/inbound')) return 'quotes-inbound';
  // /rep/quotes/:id and any sub-mode (?mode=review, ?mode=pricing)
  if (pathname.startsWith('/rep/quotes')) return 'quotes';
  if (pathname.startsWith('/rep/customers')) return 'customers';
  if (pathname.startsWith('/rep/profile')) return 'profile';
  if (pathname.startsWith('/rep/settings') || pathname.startsWith('/settings')) return 'settings';
  // /dashboard shared route used by reps , treat as inbound
  if (pathname === '/dashboard') return 'quotes-inbound';
  // Default to inbound
  return 'quotes-inbound';
}

// ─── Mobile bottom nav (Justin's nav ruling, 2026-07-29) ─────────────────────
//
// Below 768px the rep <aside> (RepNewspaperSidebar) is not rendered at all —
// its hard-coded 280px/64px flex width was stealing horizontal space from the
// content column on real handsets (280px of a 390px viewport left ~110px for
// the quote itself). Below that breakpoint the content column takes full
// width and a fixed bottom nav (Today / Inbound / Quotes) takes over,
// reusing ChefTabBar's fixed-bottom + scroll-hide chrome rather than a
// second parallel bottom-bar component — see chef/ChefTabBar.tsx.
//
// EXCEPTION (part of the ruling, Ch XXII — secondary actions never compete
// with Send): on the quote-detail screens (/quote-builder, /export-finalize)
// Send owns the bottom exclusively, so the bottom nav is suppressed there.
// Those two routes are the only surfaces reached under RepLayout that render
// the quote itself with a Send-driving action in the fixed bottom area
// (ExportFinalizePage's "Email Quote to Chef" bar; QuoteBuilderPage's
// "Continue to Export" FAB) — /map-ingredients and /start-new-quote are
// process steps, not the quote detail, so they keep the bottom nav.
const REP_BOTTOM_TABS: TabDef[] = [
  { id: 'today',    label: 'Today',   target: 'rep-tab-today' },
  { id: 'inbound',  label: 'Inbound', target: 'rep-tab-inbound' },
  { id: 'quotes',   label: 'Quotes',  target: 'rep-tab-quotes' },
];

function repBottomTabFromPath(pathname: string): string {
  if (pathname === '/dashboard') return 'today';
  if (pathname.startsWith('/rep/quotes/history')) return 'quotes';
  if (pathname.startsWith('/rep/quotes')) return 'inbound';
  if (
    pathname.startsWith('/start-new-quote') ||
    pathname.startsWith('/map-ingredients') ||
    pathname.startsWith('/quote-builder') ||
    pathname.startsWith('/export-finalize')
  ) {
    return 'inbound';
  }
  return '';
}

// Quote-detail / Review-and-Send surfaces — Send wins, bottom nav suppressed.
export function isRepQuoteDetailRoute(pathname: string): boolean {
  return pathname.startsWith('/quote-builder') || pathname.startsWith('/export-finalize');
}

// Environments without matchMedia (some jsdom/test setups) default to
// desktop so pre-existing desktop-oriented tests/behavior are unaffected —
// real browsers all support matchMedia, so this only matters in test envs.
function supportsMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(
    supportsMatchMedia() ? window.matchMedia('(min-width: 768px)').matches : true
  );
  useEffect(() => {
    if (!supportsMatchMedia()) return;
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

// ─── RepLayout ────────────────────────────────────────────────────────────────

const sans: React.CSSProperties = {
  fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

interface RepLayoutProps {
  // When provided, rendered instead of <Outlet />.
  // Used by the shared /dashboard route where RepLayout is not a router parent.
  children?: React.ReactNode;
}

export function RepLayout({ children }: RepLayoutProps = {}) {
  const [mode, setMode] = useState<RepSidebarMode>('open');
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDesktop = useIsDesktop();

  const repName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || 'Rep'
    : 'Rep';
  const distributorName = user?.distributor?.name ?? user?.distributor_name ?? '';
  // Session-derived role label, never hardcoded, so a rep login always reads
  // "Rep" and, should this shell ever render for another role, that role's
  // own label shows instead.
  const sessionRoleLabel = roleLabel(user?.role);

  const active = activeTabFromPath(location.pathname);
  // Below 768px the aside never renders, so "hidden" (the desktop-only
  // collapse-to-nothing mode) is meaningless there — gate it to desktop.
  const hidden = isDesktop && mode === 'hidden';
  // Quote-detail / Review-and-Send surfaces suppress the bottom nav so Send
  // owns the bottom exclusively (Justin's nav ruling, Ch XXII).
  const showBottomNav = !isDesktop && !isRepQuoteDetailRoute(location.pathname);

  const nav = (dest: string, opts?: { quoteId?: string }) => {
    if (dest === 'rep-triage' || dest === 'rep-quotes-inbound') navigate('/rep/quotes/inbound');
    else if (dest === 'rep-quotes-history') navigate('/rep/quotes/history');
    // P0: old triage view (/rep/quotes/:id) deleted , route into the canonical
    // quote-build flow instead (MapIngredientsPage loads the existing quote).
    else if (dest === 'rep-incoming' && opts?.quoteId) navigate('/map-ingredients', { state: { quoteId: opts.quoteId } });
    else if (dest === 'rep-pricing' && opts?.quoteId) navigate('/map-ingredients', { state: { quoteId: opts.quoteId } });
    else if (dest === 'rep-catalog') navigate('/distributor-admin/catalog');
    else if (dest === 'rep-customers') navigate('/rep/customers');
    else if (dest === 'rep-profile') navigate('/rep/profile');
    else if (dest === 'rep-settings') navigate('/settings');
  };

  const navBottom = (target: string) => {
    if (target === 'rep-tab-today') navigate('/dashboard');
    else if (target === 'rep-tab-inbound') navigate('/rep/quotes/inbound');
    else if (target === 'rep-tab-quotes') navigate('/rep/quotes/history');
  };

  return (
    <RepSidebarContext.Provider value={{ mode, setMode }}>
      <div
        style={{
          display: 'flex',
          position: 'relative',
          height: '100%',
          background: '#fff',
        }}
      >
        {isDesktop && !hidden && (
          <RepNewspaperSidebar
            mode={mode}
            onModeChange={setMode}
            active={active}
            onNav={nav}
            repName={repName}
            roleLabel={sessionRoleLabel}
            distributorName={distributorName}
          />
        )}

        <main
          style={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            background: '#fff',
          }}
        >
          <div
            style={{
              ...sans,
              padding: isDesktop ? '36px 40px' : '20px 16px',
              maxWidth: 1180,
              // Reserve space above the fixed bottom nav so scrolled content
              // never sits underneath it.
              paddingBottom: showBottomNav ? 84 : (isDesktop ? undefined : 20),
            }}
          >
            {children ?? <Outlet />}
          </div>
        </main>

        {isDesktop && hidden && (
          <button
            type="button"
            onClick={() => setMode('open')}
            aria-label="Show sidebar"
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              fontSize: 11,
              color: '#6B7280',
              background: '#FBFAF7',
              border: '1px solid #E8E8E8',
              borderRadius: 6,
              padding: '6px 10px',
              cursor: 'pointer',
              zIndex: 10,
              writingMode: 'vertical-rl' as React.CSSProperties['writingMode'],
            }}
          >
            › sidebar
          </button>
        )}

        {showBottomNav && (
          <ChefTabBar
            active={repBottomTabFromPath(location.pathname)}
            nav={navBottom}
            tabs={REP_BOTTOM_TABS}
          />
        )}
      </div>
    </RepSidebarContext.Provider>
  );
}
